import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { MediaBankFileModel } from '../media-bank-file.model';
import { MobileTabsModel } from '@fi-app/utilities/mobile-tabs/mobile-tabs.model';
import { MobileTabModel } from '@fi-app/utilities/mobile-tabs/mobile-tab.model';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { MediaBankPreviewComponent } from '../media-bank-preview/media-bank-preview.component';
import { FiCommentSettingType, FileType } from '@fi-shared/types';
import { MediaBankService } from '../media-bank.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ModuleType } from '@fi-shared/types/module-type';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiMobileAppHelperService } from '@fi-app/utilities/fi-services/mobile-app-helper.service';
import { StatisticService } from '@fi-app/shared/statistic/statistic.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { MediaBankListDataService } from '@fi-app/media-bank/media-bank-list-data.service';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { FiCommentsService } from '@fi-shared/fi-comments/fi-comments.service';
import { Subject } from 'rxjs';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { MoreMenuMobileSettingsModel } from '@fi-app/utilities/more-menu-mobile/more-menu-mobile-settings.model';
import { ICommentModel, ICommentsContainer } from '@fi-shared/fi-comments/fi-comments';
import { findClosest } from '@fi-shared/utils';
import { UserService } from '@fi-app/user/user.service';
import { FiItemsEditHelperService } from '@fi-shared/fi-shared-services/fi-items-edit-helper.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiCommentsListStateService } from '@fi-shared/fi-comments/fi-comments-list/services/fi-comments-list-state.service';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';

@Component({
    selector: 'fi-media-bank-view',
    templateUrl: './media-bank-view.component.html',
    styleUrls: ['./media-bank-view.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-media-bank-view' },
    standalone: false,
})
export class MediaBankViewComponent implements OnInit, OnDestroy, OnChanges {
    @Input('mediaBankFile') MediaBankFile: MediaBankFileModel;
    @Input() isFromMediaBankModalDialog: boolean = false;
    @Input() commentId: number;
    @Input() focusNewComment: boolean;
    @ViewChild('commentScrollTo', { static: false }) commentScrollToElement: ElementRef;
    @ViewChild('mediaPreview', { static: false }) mediaPreview: MediaBankPreviewComponent;

    MediaBankAccessRight: boolean;
    MobileTabs: MobileTabsModel;
    EditTabs: EditTabsModel;
    MoreMenuItems: MoreMenuMobileSettingsModel;
    ShowComments: boolean;
    ModuleType;
    ObjectTypes;
    FiStateNames = FiStateNames;

    commentsContainer: ICommentsContainer;

    private visitedTimeout;

    constructor(
        private appTextService: AppTextService,
        private stateAccessCheckService: StateAccessCheckService,
        private scrollToElementAnimateService: FiScrollToElementAnimateService,
        private detectDevicesService: DetectDevicesService,
        private mediaBankService: MediaBankService,
        private mediaBankHelperService: MediaBankHelperService,
        private stateService: FiStateService,
        private mediaStorageService: FiMediaStorageService,
        private mobileAppHelperService: FiMobileAppHelperService,
        private statisticService: StatisticService,
        private mediaBankListDataService: MediaBankListDataService,
        private fiItemsDeleteHelperService: FiItemsDeleteHelperService,
        private fiItemsEditHelperService: FiItemsEditHelperService,
        private commentsService: FiCommentsService,
        private cdr: ChangeDetectorRef,
        private userService: UserService,
        private userDataService: FiUserDataService,
        private commentsListStateService: FiCommentsListStateService,
        private fiPrintDownloadService: FiPrintDownloadService,
    ) {
        this.ModuleType = ModuleType;
        this.ObjectTypes = ObjectTypes;
    }

    protected isDownloadingDisabled$ = this.fiPrintDownloadService.isDownloadingInProcess$;

    protected comments: ICommentModel[] = [];

    ngOnInit() {
        this.MediaBankAccessRight =
            this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.MediaBank.Edit) &&
            this.MediaBankFile.AccessRightFilterCollection.HasEditAccess;

        this.MobileTabs = new MobileTabsModel('');
        this.MobileTabs.Tabs.push(new MobileTabModel(this.appTextService.get('common.files'), 'MediaBankTab'));
        this.MobileTabs.Tabs.push(new MobileTabModel(this.appTextService.get('common.more'), 'MoreTab'));
        this.EditTabs = new EditTabsModel(this.MediaBankFile.Name, this.appTextService.get('back'));

        this.ShowComments =
            !this.isFromMediaBankModalDialog &&
            this.MediaBankFile.MediaBankFileSetting.CommentSettingType == FiCommentSettingType.AllowAllComments;

        this.visitedTimeout = setTimeout(() => {
            this.mediaBankService.MarkMediaBankFileAsVisited(this.MediaBankFile.Id);
        }, 4000);

        this.statisticService.Visit(ModuleType.MediaBank, ObjectTypes.MediaBank, this.MediaBankFile.Id);

        this.commentsService.getComments(this.MediaBankFile.Id, ObjectTypes.MediaBank).then((comments) => {
            this.comments = comments;
        });

        this.initMoreMenuMobile();
    }

    get commentsCount(): number {
        return this.commentsListStateService.getCommentsCount(ObjectTypes.MediaBank, this.MediaBankFile.Id);
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    initMoreMenuMobile() {
        if (this.MediaBankAccessRight) {
            // https://chainformation.atlassian.net/browse/FI-5578
            this.MoreMenuItems = new MoreMenuMobileSettingsModel();
            this.MoreMenuItems.AddListItem(this.appTextService.get('common.edit'), () => {
                this.editMediaBankFile();
            });
            this.MoreMenuItems.AddListItem(this.appTextService.get('common.delete'), () => {
                this.deleteMediaBankFile();
            });
        }
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        clearTimeout(this.visitedTimeout);
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    GetFileExtension(extension) {
        if (extension) return extension.substring(1).toUpperCase();
    }

    GetFileTypeName(fileType: number) {
        return this.mediaBankHelperService.getFileTypeName(fileType);
    }

    GetFileDimension(width, height) {
        return String.format('{0} * {1} {2}', width, height, 'pixels');
    }

    GetFileDPI(dpi) {
        return String.format('{0} {1}', dpi, 'DPI');
    }

    GetFileColorDepth(colorDepth) {
        return String.format('{0} {1}', colorDepth, 'bit');
    }

    mediaPreviewAfterRender = new EventEmitter();
    _mediaPreviewAfterRender() {
        this.mediaPreviewAfterRender.emit();
    }

    ngOnChanges(changes) {}

    toggleComments() {
        setTimeout(() => {
            this.scrollToElementAnimateService.scrollToElementBottom(
                this.commentScrollToElement.nativeElement,
                this.getCommentScrollElement(),
                20,
            );
            this.commentsListStateService.emitCommentUpdateConnectionLine(ObjectTypes.MediaBank, this.MediaBankFile.Id);
        });
    }

    getCommentScrollElement(): HTMLElement {
        return this.isMobilePhone()
            ? findClosest(this.commentScrollToElement.nativeElement, '.MobileTabPageContainer')
            : findClosest(this.commentScrollToElement.nativeElement, '[fi-scroll-root]');
    }

    getHighlightCommentId() {
        return this.stateService.params.commentId;
    }

    isMobileApp() {
        return this.mobileAppHelperService.mobileAppMode;
    }

    getMediaBankFileUrl() {
        return this.mediaStorageService.getMediaFileUrl(this.MediaBankFile);
    }

    isPdf() {
        return this.MediaBankFile.FileType === FileType.Pdf;
    }

    showSiteDesignAccessForItems() {
        return this.userDataService.showSiteDesignAccessForItems();
    }

    deleteMediaBankFile() {
        this.fiItemsDeleteHelperService
            .mediaBankDeleteConfirm(
                [this.MediaBankFile],
                this.appTextService.get('mediabank.delete_permanently_confirm'),
            )
            .then(() => {
                this.mediaBankService.DeleteMediaBankFiles([this.MediaBankFile.Id]).then(() => {
                    this.showSnackbar();
                    window.history.back();
                });
            });
    }

    protected downloadMediaBankFile() {
        this.mediaBankHelperService.downloadMediaBankFileRef(this.MediaBankFile);
    }

    private showSnackbar() {
        const restoreMediaBankFn = (restoreIds: number[]) => this.mediaBankService.RestoreMediaBankFiles(restoreIds);
        const reloadMediaBankListFn = () => this.mediaBankService.doReloadMediaBankList$();

        this.fiItemsDeleteHelperService.showDeletedObjectSnackBar(
            ObjectTypes.MediaBank,
            [this.MediaBankFile],
            this.MediaBankFile.IsDeleted,
            restoreMediaBankFn,
            reloadMediaBankListFn,
        );
    }

    editMediaBankFile() {
        this.fiItemsEditHelperService.showEditRestoreConfirm(
            this.MediaBankFile.Name,
            this.MediaBankFile.IsDeleted,
            () => this.stateService.go(FiStateNames.MediaBank.Edit, { Id: this.MediaBankFile.Id }),
            () => this.mediaBankService.RestoreMediaBankFiles([this.MediaBankFile.Id]),
        );
    }

    protected openUserProfileDialog(userId: number) {
        this.userService.openUserViewDialog(userId);
    }

    onMediaClick($event: PointerEvent) {
        $event.preventDefault();
        this.mediaBankHelperService.downloadMediaBankFileRef(this.MediaBankFile);
    }
}
